<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SubjectTeachingSelection from '@/Components/Forms/SubjectTeachingSelection.vue'
import TeachingExperienceSelection from '@/Components/Forms/TeachingExperienceSelection.vue'

const props = defineProps({
  subjects: Array,
  userSubjectIds: Array,
  levels: Array,
  userLevelIds: Array,
  examboards: Array,
  userExamboardIds: Array,
  experiences: Array,
  userExperienceIds: Array,
  teaching: Array,
})

/**
 * What the tutor teaches, one row per subject.
 *
 * This page used to edit three flat lists — subjects, levels, exam boards —
 * while the application asked subject by subject, so a tutor could describe
 * "Spanish at A Level, History at GCSE" when they applied and never again. A
 * tutor with no detailed rows arrives with their old lists expanded, which is
 * what their profile already shows.
 */
const teachingRows = ref([])
const selectedExperiences = ref([])
const teachingRef = ref(null)

const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

onMounted(() => {
  teachingRows.value = (props.teaching || []).map((row) => ({
    subject_id: row.subject_id,
    level_ids: row.level_ids ?? [],
    examboard_ids: row.examboard_ids ?? [],
  }))
  selectedExperiences.value = props.userExperienceIds || []
})

const updateSelectedExperiences = (newExperiences) => {
  selectedExperiences.value = newExperiences
}

const validateForm = () => {
  const validationErrors = {}

  if (selectedExperiences.value.length === 0) {
    validationErrors.selected_experiences = 'Please select your teaching experience level'
  }

  errors.value = validationErrors

  // The rows validate themselves, against the same rules the application uses.
  const rowsValid = teachingRef.value ? teachingRef.value.validate() : true

  return rowsValid && Object.keys(validationErrors).length === 0
}

const saveTeachingDetails = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/teaching-details', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        teaching: teachingRows.value.filter((row) => row.subject_id),
        experience_ids: selectedExperiences.value,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Map backend error keys to component error keys
        errors.value = {
          ...data.errors,
          teaching: data.errors.teaching ? data.errors.teaching[0] : null,
          selected_experiences: data.errors.experience_ids ? data.errors.experience_ids[0] : null,
        }
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Teaching details updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- What you teach, subject by subject -->
      <SubjectTeachingSelection
        ref="teachingRef"
        :subjects="subjects"
        :levels="levels"
        :examboards="examboards"
        :model-value="teachingRows"
        @update:model-value="(rows) => teachingRows = rows"
        :errors="errors"
        title="Subjects & Levels"
        description="One row per subject, with the levels and exam boards you cover for it"
      />

      <!-- Teaching Experience Selection -->
      <TeachingExperienceSelection
        :model-value="selectedExperiences"
        @update:model-value="updateSelectedExperiences"
        :experiences="experiences"
        :errors="errors"
        title="Teaching Experience"
        description="Select your teaching experience level"
        :show-shadow="false"
        view-mode="simple"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveTeachingDetails"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
